import {Injectable} from "@angular/core";
import {AuthService} from "@auth0/auth0-angular";
import {ConfigService} from "./config.service";
import {filter, take} from "rxjs/operators";
import {firstValueFrom, lastValueFrom} from 'rxjs';

@Injectable({providedIn: 'root'})
export class TokenService {

  private readonly MAX_REDIRECT_ATTEMPTS = 2;
  private readonly REDIRECT_ATTEMPT_KEY = 'auth0_redirect_attempts';
  private readonly REDIRECT_TIMESTAMP_KEY = 'auth0_redirect_timestamp';
  private readonly ATTEMPT_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

  constructor(
    private auth: AuthService,
    private configService: ConfigService,
  ) {
  }

  private _token: string = '';

  // Les deux getters retournent le même token (audience commune)
  get tokenApiTicketing(): string {
    return this._token;
  }

  get tokenApiControl(): string {
    return this._token;
  }

  async createTokens(): Promise<void> {
    console.log('Starting token creation process...');

    this.cleanupExpiredAttempts();

    console.log('Waiting for user authentication...');
    await firstValueFrom(this.auth.isAuthenticated$.pipe(filter(Boolean), take(1)));
    console.log('User authenticated successfully');

    try {
      await this.getToken();
      console.log('Token obtained successfully');
    } catch (e) {
      console.error('Failed to obtain token', e);
      throw e;
    }

    this.resetRedirectAttempts();
    console.log('Token creation completed');
  }

  waitForTokenApiControl(maxWaitMs: number = 10000): Promise<void> {
    return this.waitForToken(maxWaitMs);
  }

  async waitForTokenApiTicketing(maxWaitMs: number = 10000): Promise<void> {
    return this.waitForToken(maxWaitMs);
  }

  private waitForToken(maxWaitMs: number = 10000): Promise<void> {
    const start = Date.now();
    return new Promise((resolve, reject) => {
      const check = () => {
        if (this._token && this._token.length > 0) {
          console.log('Token ready');
          resolve();
          return;
        }
        if (Date.now() - start >= maxWaitMs) {
          console.error(`Token not ready after ${maxWaitMs}ms`);
          reject(new Error('Token not ready'));
          return;
        }
        setTimeout(check, 100);
      };
      check();
    });
  }

  private async getToken(): Promise<void> {
    const audience = this.configService.auth0ApiAudience;
    console.log('Requesting token for audience:', audience);

    // Stratégie 1: Tentative silencieuse normale avec cache
    try {
      console.log('Strategy 1: Normal silent token request with cache');
      const token = await lastValueFrom(
        this.auth.getAccessTokenSilently({
          authorizationParams: {
            audience: audience,
          }
        })
      );

      this._token = `Bearer ${token}`;
      console.log('Token obtained successfully (strategy 1)');
      return;
    } catch (e1: any) {
      console.log('Strategy 1 failed:', e1?.error || e1?.message);
    }

    // Stratégie 2: Forcer le refresh sans utiliser le cache
    try {
      console.log('Strategy 2: Silent request without cache (force refresh)');
      const token = await lastValueFrom(
        this.auth.getAccessTokenSilently({
          authorizationParams: {
            audience: audience,
          },
          cacheMode: 'off'
        })
      );

      this._token = `Bearer ${token}`;
      console.log('Token obtained successfully (strategy 2)');
      return;
    } catch (e2: any) {
      console.log('Strategy 2 failed:', e2?.error || e2?.message);
    }

    // Stratégie 3: Augmenter le timeout
    try {
      console.log('Strategy 3: Silent request with extended timeout');
      const token = await lastValueFrom(
        this.auth.getAccessTokenSilently({
          authorizationParams: {
            audience: audience,
          },
          timeoutInSeconds: 60
        })
      );

      this._token = `Bearer ${token}`;
      console.log('Token obtained successfully (strategy 3)');
      return;
    } catch (e3: any) {
      console.error('All silent strategies failed. Last error:', {
        error: e3?.error,
        errorDescription: e3?.error_description,
        message: e3?.message
      });

      if (e3?.error === 'consent_required' ||
        e3?.error === 'login_required' ||
        e3?.error === 'interaction_required' ||
        e3?.error === 'missing_refresh_token' ||
        e3?.message?.includes('login_required')) {

        const attempts = this.getRedirectAttempts();
        console.log(`Consent/interaction required. Current redirect attempts: ${attempts}`);

        if (attempts >= this.MAX_REDIRECT_ATTEMPTS) {
          console.error(`Maximum redirect attempts (${this.MAX_REDIRECT_ATTEMPTS}) reached. Stopping to prevent infinite loop.`);
          this.resetRedirectAttempts();
          throw new Error(`Auth0 consent failed after ${this.MAX_REDIRECT_ATTEMPTS} attempts. Please contact support.`);
        }

        this.incrementRedirectAttempts();
        console.log(`Redirecting to Auth0 for consent (attempt ${attempts + 1}/${this.MAX_REDIRECT_ATTEMPTS})...`);

        this.auth.loginWithRedirect({
          authorizationParams: {
            audience: audience
          }
        });

        throw new Error('Redirecting to Auth0 for consent');
      } else {
        throw e3;
      }
    }
  }

  private getRedirectAttempts(): number {
    try {
      const attempts = localStorage.getItem(this.REDIRECT_ATTEMPT_KEY);
      return attempts ? parseInt(attempts, 10) : 0;
    } catch (e) {
      console.error('Failed to read redirect attempts from localStorage', e);
      return 0;
    }
  }

  private incrementRedirectAttempts(): void {
    try {
      const currentAttempts = this.getRedirectAttempts();
      localStorage.setItem(this.REDIRECT_ATTEMPT_KEY, (currentAttempts + 1).toString());
      localStorage.setItem(this.REDIRECT_TIMESTAMP_KEY, Date.now().toString());
    } catch (e) {
      console.error('Failed to increment redirect attempts in localStorage', e);
    }
  }

  private resetRedirectAttempts(): void {
    try {
      localStorage.removeItem(this.REDIRECT_ATTEMPT_KEY);
      localStorage.removeItem(this.REDIRECT_TIMESTAMP_KEY);
      console.log('Redirect attempts counter reset');
    } catch (e) {
      console.error('Failed to reset redirect attempts in localStorage', e);
    }
  }

  private cleanupExpiredAttempts(): void {
    try {
      const timestampStr = localStorage.getItem(this.REDIRECT_TIMESTAMP_KEY);
      if (timestampStr) {
        const timestamp = parseInt(timestampStr, 10);
        const now = Date.now();
        if (now - timestamp > this.ATTEMPT_TIMEOUT_MS) {
          console.log('Redirect attempts expired, resetting counter');
          this.resetRedirectAttempts();
        }
      }
    } catch (e) {
      console.error('Failed to cleanup expired attempts', e);
    }
  }

}
